import { z } from "zod";

// ─── Resolve a prospect from a LinkedIn URL (global-search "By URL" tab) ──────
// POST v1/list/person/from-linkedin-url — resolves a single LinkedIn profile URL into a saved
// prospect. The UI adds profiles one at a time, so it fires one request per URL. The response
// is the resolved prospect itself (no resolved/failed wrapper); a URL that can't be fetched
// returns 400, which the caller treats as that URL having failed. After the user picks which
// prospects to add, the returned `id` (prospectId) is passed to the existing
// v1/list/person/sync/bulk(/:id) endpoints.

export const ResolveProspectFromLinkedinUrlRequestSchema = z.object({
	linkedinUrl: z.string().trim().min(1),
});

// Permissive (.passthrough()) — the backend serializes the full Organization model; only the
// commonly-used fields are typed here. Add more (e.g. keywords, foundedYear, coverImage,
// rawAddress, companyLinkedinId) as the FE needs them; extras pass through untyped.
export const ResolvedLinkedinOrganizationSchema = z
	.object({
		id: z.number().nullish(),
		name: z.string().nullish(),
		websiteUrl: z.string().nullish(),
		primaryDomain: z.string().nullish(),
		linkedinUrl: z.string().nullish(),
		logoUrl: z.string().nullish(),
		industry: z.string().nullish(),
		shortDescription: z.string().nullish(),
		city: z.string().nullish(),
		state: z.string().nullish(),
		country: z.string().nullish(),
		estimatedNumEmployees: z.number().nullish(),
		employeesFrom: z.number().nullish(),
		employeesTo: z.number().nullish(),
		followerCount: z.number().nullish(),
	})
	.passthrough();

// All fields optional/nullable — backend serializes a Prospect. `id` is the prospectId to pass
// to the add-to-list bulk endpoints.
export const ResolvedLinkedinProspectSchema = z.object({
	id: z.number().nullish(),
	name: z.string().nullish(),
	firstName: z.string().nullish(),
	lastName: z.string().nullish(),
	title: z.string().nullish(),
	seniority: z.string().nullish(),
	headline: z.string().nullish(),
	image: z.string().nullish(),
	linkedinUrl: z.string().nullish(),
	urn: z.string().nullish(),
	about: z.string().nullish(),
	country: z.string().nullish(),
	city: z.string().nullish(),
	state: z.string().nullish(),
	language: z.string().nullish(),
	connections: z.number().nullish(),
	followers: z.number().nullish(),
	organization: ResolvedLinkedinOrganizationSchema.nullish(),
	// True when this prospect already exists as a contact in the user's company. The UI then
	// treats it like an existing contact (added by prospectId, no LinkedIn resolution credit),
	// matching how the other global-search tabs handle their `contacts` bucket.
	isExistingContact: z.boolean().nullish(),
});

// The response is the resolved prospect itself — see ResolvedLinkedinProspectSchema above.

export type ResolveProspectFromLinkedinUrlRequest = z.infer<
	typeof ResolveProspectFromLinkedinUrlRequestSchema
>;
export type ResolvedLinkedinProspect = z.infer<typeof ResolvedLinkedinProspectSchema>;
export type ResolvedLinkedinOrganization = z.infer<typeof ResolvedLinkedinOrganizationSchema>;
// The endpoint returns a single resolved prospect.
export type ResolveProspectFromLinkedinUrlResponse = ResolvedLinkedinProspect;
